import React, { useCallback, useEffect, useState } from 'react';
import { useLingui } from '@lingui/react/macro';

import {
  BRIGHT_HORIZONS_CREDITS_DONT_APPLY,
  BRIGHT_HORIZONS_CREDITS_DONT_APPLY_TEXT,
} from '@rover/components/src/callouts/CalloutBadge/CalloutBadge.constants';
import usePriceDisplayInfo from '@rover/components/src/cards/SearchResultCard/PriceColumn/usePriceDisplayInfo';
import { DEFAULT_CURRENCY_ISO } from '@rover/constants/src/i18n/language';
import { Box, Flex, Text } from '@rover/kibble/core';
import { Badge } from '@rover/kibble/official-patterns/Badge';
import { CLOSE_TIMEOUT } from '@rover/kibble/official-patterns/BottomSheet/BottomSheet';
import useLocationContext from '@rover/react-lib/src/context/useLocationContext';
import { useCurrentUser } from '@rover/rsdk/src/modules/Data/Core/CurrentUser';

import ContactButton from '../../../components/ContactButton';
import { useRevampBrightHorizonsBanner } from '../../hooks/useRevampBrightHorizonsBanner';
import { useRevampCta } from '../../hooks/useRevampCta';
import { useRevampCtaCommon } from '../../hooks/useRevampCtaCommon';
import { useRevampProfileContext } from '../../hooks/useRevampProfileContext';
import { useRevampSelectedDates } from '../../hooks/useRevampSelectedDates';
import { useUpdateSelectedDates } from '../../hooks/useUpdateSelectedDates';
import { BHSitterProfileBanner } from '../BHSitterProfileBanner/BHSitterProfileBanner';

import RevampDatesSheet, { DateSelection } from './RevampDatesSheet';
import RevampPriceDisplaySheet from './RevampPriceDisplaySheet';
import { CTAPropsCommon } from './RevampServicesCta';
import { getRangeEndDate, getSitterSlugFromWebUrl } from './utils';

type Props = CTAPropsCommon & {
  handleOpenSheet: () => void;
  selectedDate: Date;
  selectedEndDate?: Date;
  hasDateRange?: boolean;
};

type PendingAction = 'service' | 'dates' | null;

const RevampMobileCtaBarPriceDisplay = ({
  handleOpenSheet,
  priceTransparencyLevel,
  locale,
  petCount,
  petType,
  bookingFeePercentage,
  selectedDate,
  selectedEndDate,
  hasDateRange,
}: Props): JSX.Element => {
  const { i18n, t } = useLingui();
  const { contactUrl, showSittersRates } = useRevampProfileContext();
  const {
    sitterData,
    selectedService,
    serviceDisplayName,
    isBrightHorizonsUser,
    isSelectedServiceUnapproved,
    isContactUnavailable,
  } = useRevampCta();
  const { rate, price } = useRevampCtaCommon(
    priceTransparencyLevel,
    petType,
    petCount,
    selectedService
  );
  const currentUser = useCurrentUser();
  const location = useLocationContext();

  const updateSelectedDates = useUpdateSelectedDates();

  const [isExpanded, setIsExpanded] = useState(false);
  const [isDatesSheetOpen, setIsDatesSheetOpen] = useState(false);
  const [pendingAction, setPendingAction] = useState<PendingAction>(null);

  const handleExpand = useCallback(() => setIsExpanded(true), []);
  const handleCollapse = useCallback(() => setIsExpanded(false), []);

  // The sheet locks page scroll while open, so both actions wait until it has
  // fully closed before running. Opening the dates calendar from within an open
  // sheet would otherwise leave two sheets fighting over the scroll lock.
  const handleServicePress = useCallback(() => {
    setPendingAction('service');
    setIsExpanded(false);
  }, []);
  const handleDatesPress = useCallback(() => {
    setPendingAction('dates');
    setIsExpanded(false);
  }, []);

  // Collapsing only starts the close transition: the outgoing sheet stays mounted for
  // it and restores focus as it unmounts, which would pull focus back out of a
  // successor opened any sooner.
  useEffect(() => {
    if (isExpanded || !pendingAction) return undefined;

    const timeout = setTimeout(() => {
      if (pendingAction === 'service') handleOpenSheet();
      else setIsDatesSheetOpen(true);
      setPendingAction(null);
    }, CLOSE_TIMEOUT);

    return () => clearTimeout(timeout);
  }, [isExpanded, pendingAction, handleOpenSheet]);

  const handleDatesSheetClose = useCallback(() => setIsDatesSheetOpen(false), []);
  const handleDatesSave = useCallback(
    (selection: DateSelection) => updateSelectedDates(selection),
    [updateSelectedDates]
  );

  const priceInCredits = rate?.priceInCredits != null ? Number(rate.priceInCredits) : null;
  const shouldShowPriceInCredits =
    isBrightHorizonsUser && !showSittersRates && priceInCredits != null;

  const { brightHorizonsBanner, isShowingBhBanner } = useRevampBrightHorizonsBanner(
    petType,
    petCount
  );

  const { formattedPrice, beforePriceText, afterPriceText, showBrightHorizonsCalloutBadge } =
    usePriceDisplayInfo({
      currencyCode: rate?.pricePerUnitObj.currencyIso || DEFAULT_CURRENCY_ISO,
      priceTransparencyLevel,
      locale,
      price: price ?? '',
      priceInCredits,
      shouldShowPriceInCredits,
      showFromText: !shouldShowPriceInCredits,
      unitAsSentence: rate?.unitAsSentence ?? '',
      isBHUser: isBrightHorizonsUser,
    });

  const {
    allowsLooseDates,
    allowsRecurring,
    looseDates,
    isRecurring,
    weekDays,
    hasDates,
    datesLabel: selectedDatesLabel,
  } = useRevampSelectedDates(locale, selectedDate, selectedEndDate, hasDateRange);
  const datesLabel = hasDates ? selectedDatesLabel : t`Add dates`;
  const serviceLabel = serviceDisplayName ?? t`Select a service`;
  // The accessible name repeats the badge verbatim, so voice control can target the
  // control by the label it shows.
  const comingSoonLabel = t`Coming Soon`;

  return (
    <>
      {showBrightHorizonsCalloutBadge && (
        <Box pb="2x">
          <Badge
            {...BRIGHT_HORIZONS_CREDITS_DONT_APPLY}
            text={i18n._(BRIGHT_HORIZONS_CREDITS_DONT_APPLY_TEXT)}
          />
        </Box>
      )}

      {isShowingBhBanner && <BHSitterProfileBanner brightHorizonsBanner={brightHorizonsBanner} />}

      <Flex alignItems="center" gap="4x">
        <Flex
          gap="2x"
          webAs="button"
          flexDirection="column"
          alignItems="flex-start"
          justifyContent="center"
          alignSelf="stretch"
          flexGrow="1"
          border="none"
          padding="0x"
          data-testid="revamp-mobile-cta-bar-details"
          onPress={handleExpand}
          aria-haspopup="dialog"
          aria-expanded={isExpanded}
          aria-label={
            isSelectedServiceUnapproved
              ? t`${comingSoonLabel}, ${serviceLabel}, ${datesLabel}. See booking details`
              : t`${beforePriceText} ${formattedPrice} ${afterPriceText}, ${serviceLabel}, ${datesLabel}. See booking details`
          }
          sx={{ minWidth: 0, background: 'transparent', cursor: 'pointer', textAlign: 'left' }}
        >
          {isSelectedServiceUnapproved ? (
            <Badge
              boxProps={{ testID: 'cta-coming-soon-badge', alignSelf: 'flex-start' }}
              text={comingSoonLabel}
              variant="default"
            />
          ) : (
            rate &&
            price && (
              <Text
                size="200"
                fontWeight="regular"
                textColor="secondary"
                data-testid="revamp-mobile-cta-bar-price"
                aria-hidden="true"
              >
                {beforePriceText && `${beforePriceText} `}
                <Text
                  size="300"
                  fontWeight="semibold"
                  textColor="price"
                  sx={{ textDecoration: 'underline' }}
                >
                  {formattedPrice}
                </Text>
                {` ${afterPriceText}`}
              </Text>
            )
          )}

          <Flex alignItems="center" gap="2x" aria-hidden="true">
            <Text
              size="200"
              fontWeight="semibold"
              data-testid="revamp-mobile-cta-bar-service"
              sx={{ textDecoration: 'underline' }}
            >
              {serviceLabel}
            </Text>
            <Text size="200" textColor="secondary">
              ·
            </Text>
            <Text size="200" textColor="secondary" data-testid="revamp-mobile-cta-bar-dates">
              {datesLabel}
            </Text>
          </Flex>
        </Flex>

        <ContactButton
          sitterInfo={sitterData}
          sitterSlug={getSitterSlugFromWebUrl(sitterData.webUrl)}
          contactUrl={contactUrl}
          isLoggedIn={currentUser.isAuthenticated}
          location={location}
          pagePosition="sticky-footer"
          shouldShowRevampLayout
          disabled={isContactUnavailable}
          width="auto"
          px="8x"
          minWidth="140px"
          flexShrink="0"
        />
      </Flex>

      <RevampPriceDisplaySheet
        isOpen={isExpanded}
        onClose={handleCollapse}
        onServicePress={handleServicePress}
        onDatesPress={handleDatesPress}
        priceTransparencyLevel={priceTransparencyLevel}
        locale={locale}
        petCount={petCount}
        petType={petType}
        bookingFeePercentage={bookingFeePercentage}
        selectedDate={selectedDate}
        selectedEndDate={selectedEndDate}
        hasDateRange={hasDateRange}
      />

      <RevampDatesSheet
        isOpen={isDatesSheetOpen}
        onClose={handleDatesSheetClose}
        onSave={handleDatesSave}
        locale={locale}
        startDate={hasDateRange ? selectedDate : undefined}
        endDate={hasDateRange ? getRangeEndDate(selectedDate, selectedEndDate) : undefined}
        dates={looseDates}
        isRecurring={isRecurring}
        weekDays={weekDays}
        allowsLooseDates={allowsLooseDates}
        allowsRecurring={allowsRecurring}
      />
    </>
  );
};

export default RevampMobileCtaBarPriceDisplay;
